import {
    ActionReducerMapBuilder,
    Draft,
    SerializedError,
    createAsyncThunk,
    createSlice,
} from '@reduxjs/toolkit';
import { IAction, SliceProps } from '../../store';
import {
    urlParamReplicaId,
    urlParamUniversalId,
    urlProjectDocumentReleaseStatusInfo,
} from '../../../Constants';
import { apiFetch } from '../../../util/ProjectUtils';

enum IReleaseStatusAccessRightsEnum {
    Write = 'write',
    Read = 'read',
    None = 'none',
}
export enum AdditionalDocStatusEnum {
    Activate = 'activate',
    Deactivate = 'deactivate',
    Release = 'release',
    RevokeRelease = 'revokeRelease',
}

const mapStatuses = (statuses: IGetStatusesResponse) => {
    const {
        accessRights,
        activateAllowed,
        deactivateAllowed,
        releaseAllowed,
        revokeReleaseAllowed,
    } = statuses.releaseStatusInfo;

    if (accessRights !== IReleaseStatusAccessRightsEnum.Write) return [];

    const statusesArray = [
        ...(activateAllowed ? [AdditionalDocStatusEnum.Activate] : []),
        ...(deactivateAllowed ? [AdditionalDocStatusEnum.Deactivate] : []),
        ...(releaseAllowed ? [AdditionalDocStatusEnum.Release] : []),
        ...(revokeReleaseAllowed ? [AdditionalDocStatusEnum.RevokeRelease] : []),
    ];

    return statusesArray;
};
/**
 * Initial slice state value.
 */
const initialState: IProjectDocumentChangeReleaseStatusState = {
    universalId: null,
    referencedMainDocId: null,
    isDialogOpen: false,
    loadingStatusOptions: false,
    statusOptions: [],
    isStatusChanged: false,
    loading: false,
    error: null,
};

const reducers = {
    openChangeReleaseStatusDialog(
        state: IProjectDocumentChangeReleaseStatusState,
        action: IAction<{ universalId: string; referencedMainDocId: string }>,
    ) {
        state.isDialogOpen = true;
        state.universalId = action.payload.universalId;
        state.referencedMainDocId = action.payload.referencedMainDocId;
    },

    closeChangeReleaseStatusDialog() {
        return { ...initialState };
    },

    resetChangeReleaseStatusErrorState(state: IProjectDocumentChangeReleaseStatusState) {
        state.error = null;
    },
};
/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Get status options for Change Additional Document Status Dialog
     */
    getAdditionalDocumentStatuses: async (
        config: {
            replicaId: string;
        },
        thunkApi,
    ): Promise<IGetStatusesResponse> => {
        const { replicaId } = config;
        const { universalId } = thunkApi.getState().projectDocumentChangeReleaseStatus;
        try {
            const url = urlProjectDocumentReleaseStatusInfo
                .replace(urlParamReplicaId, replicaId)
                .replace(urlParamUniversalId, universalId);

            const response = await apiFetch(url);

            return response.ok
                ? Promise.resolve(response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    onConfirmChangeAdditionalDocumentStatus: async (
        config: {
            replicaId: string;
            command: AdditionalDocStatusEnum;
            comment: string;
        },
        thunkApi,
    ): Promise<unknown> => {
        try {
            const { replicaId, command, comment } = config;
            const { universalId } = thunkApi.getState().projectDocumentChangeReleaseStatus;

            const formData = new FormData();
            formData.append(
                'releaseStatusInfo',
                JSON.stringify({
                    command,
                    comment,
                }),
            );

            const url = urlProjectDocumentReleaseStatusInfo
                .replace(urlParamReplicaId, replicaId)
                .replace(urlParamUniversalId, universalId);

            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };

            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectDocumentChangeReleaseStatusState>) => {
        /**
         * Reducers for the 'getAdditionalDocumentStatuses' thunk.
         */
        builder.addCase(
            getAdditionalDocumentStatuses.pending,
            (state: Draft<IProjectDocumentChangeReleaseStatusState>) => {
                state.loadingStatusOptions = true;
                state.error = null;
            },
        );

        builder.addCase(
            getAdditionalDocumentStatuses.fulfilled,
            (
                state: Draft<IProjectDocumentChangeReleaseStatusState>,
                action: IAction<IGetStatusesResponse>,
            ) => {
                state.loadingStatusOptions = false;
                state.error = null;

                state.statusOptions = mapStatuses(action.payload);
            },
        );

        builder.addCase(
            getAdditionalDocumentStatuses.rejected,
            (
                state: Draft<IProjectDocumentChangeReleaseStatusState>,
                action: IAction<SerializedError>,
            ) => {
                state.loadingStatusOptions = false;
                state.error = action.error;
            },
        );

        /**
         * Reducers for the 'onConfirmChangeAdditionalDocumentStatus' thunk.
         */
        builder.addCase(
            onConfirmChangeAdditionalDocumentStatus.pending,
            (state: Draft<IProjectDocumentChangeReleaseStatusState>) => {
                state.loading = true;
                state.error = null;
            },
        );

        builder.addCase(
            onConfirmChangeAdditionalDocumentStatus.fulfilled,
            (state: Draft<IProjectDocumentChangeReleaseStatusState>) => {
                state.isStatusChanged = true;

                state.loading = false;
                state.error = null;
            },
        );

        builder.addCase(
            onConfirmChangeAdditionalDocumentStatus.rejected,
            (
                state: Draft<IProjectDocumentChangeReleaseStatusState>,
                action: IAction<SerializedError>,
            ) => {
                state.loading = false;
                state.error = action.error;
            },
        );
    },
};

export const onConfirmChangeAdditionalDocumentStatus = createAsyncThunk(
    `${urlProjectDocumentReleaseStatusInfo}-post`,
    thunks.onConfirmChangeAdditionalDocumentStatus,
);

export const getAdditionalDocumentStatuses = createAsyncThunk(
    `${urlProjectDocumentReleaseStatusInfo}-get`,
    thunks.getAdditionalDocumentStatuses,
);

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IProjectDocumentChangeReleaseStatusState extends SliceProps {
    universalId: string;
    referencedMainDocId: string;
    isDialogOpen: boolean;
    loadingStatusOptions: boolean;
    statusOptions: AdditionalDocStatusEnum[];
    isStatusChanged: boolean;
}

interface IGetStatusesResponse {
    releaseStatusInfo: {
        accessRights: IReleaseStatusAccessRightsEnum;
        activateAllowed: boolean;
        deactivateAllowed: boolean;
        releaseAllowed: boolean;
        revokeReleaseAllowed: boolean;
    };
}

/**
 * Create the redux slice.
 */
export const projectDocumentChangeReleaseStatusSlice = createSlice({
    name: 'projectDocumentChangeReleaseStatus',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export functions from slice actions.
 */
export const {
    openChangeReleaseStatusDialog,
    closeChangeReleaseStatusDialog,
    resetChangeReleaseStatusErrorState,
} = projectDocumentChangeReleaseStatusSlice.actions;

/**
 * Export slice reducer as default.
 */
export default projectDocumentChangeReleaseStatusSlice.reducer;
